import { ISharePointParameters } from "../interfaces/ISharePointParameters";
import { ITag } from "@fluentui/react";
import { CopyMoveErrors, FileSortOptionKeys, piiConfig, sharepoint, sharepointType } from "../config/constants";
import { IFile } from "../interfaces/IFile";
import { IPager } from "../interfaces/IPager";
import { IFilePreCommitRequest } from "../interfaces/IFilePreCommitRequest";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { logger } from "../../../utilities/logger";
import { axiosRetryGet, axiosRetryPost } from "../../../utilities/AxiosRetryUtil";
import { normalizeUserIdList } from "../../../utilities/UserIdListHelper";
import { IViewInfo } from "@pnp/sp/views";
import { IBasePermissions, IListInfo, ISearchResponse, PermissionKind, sp } from "@pnp/sp/presets/all";
import { FilterSection, currentSharepointFolderUserPermissions } from "../redux/models/reduxModels";
import { ISortOption } from "../interfaces/ISortOption";
import { encodePath, escapeODataString, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { ComponentTypes } from "../config/config";
import { IPageSettings } from "../interfaces/IPageSettings";
import { IOneDriveRootDetails } from "../interfaces/IOneDriveRootDetails";
import { getListItemWebUrl } from "./GraphService";
import { forEachSharePointItemPage } from "./sharePointItemPaging";
import { buildShareLinkRequest, readSharePointErrorMessage, readSharingLinkUrl } from "./sharingLinkRequests";
import { mergeSharedWithPrincipals } from "../helpers/sharingPrincipalsHelper";
import { ICustomFieldDefinition, parseCustomFieldsEnvelope, resolveCustomFieldsColumnInternalName } from "../components/helpers/customFieldsHelper";
import { AxiosResponse } from "axios";
import { IOfficeContextApp } from "../components/helpers/officeHelper";
import { IHubSite } from "../interfaces/IHubSite";
import { ICreateCopyJobsRequest } from "../interfaces/ICreateCopyJobsRequest";
import { FolderPreCommitRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck } from "./ApiService";
import { parseString } from "xml2js";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { ILibraryDefault } from "../interfaces/ILibraryDefaults";
import { GetIANATimeZoneName } from "../../../utilities/TimeZone/SharePointTimeZones";
import authModule from "../../../utilities/AuthModule";
import { NewDocumentTemplate } from "../interfaces/IDocumentTemplate";
import { getExtension, removeExtension } from "../components/helpers/stringHelper";
import { tryGetPathname } from "../components/helpers/urlPathname";
import { buildCopyJobSourcePaths, destinationHoldsQueuedRows, escapeDestinationPathChars } from "../components/helpers/copyJobSourcePaths";
import { ISiteUser } from "../interfaces/ISiteUser";
import { buildLinearRecursiveCAML, escapeXml } from "../../../contentManager/Panels/CMFileFilterPanel/FileFilterHelper";
import { getFieldWireType } from "../../../contentManager/Panels/CMFileFilterPanel/preselectFieldMatching";
import { encodeFilterValue } from "./filterValueEncoding";
import { ISiteLocaleInfo } from "../interfaces/ISiteLocaleInfo";
import * as lcid from "../../../utilities/lcid/lcid";
import { ISearchResultCell } from "../interfaces/ISitesSearchResponse";
import { ISiteInfo } from "../interfaces/ISiteInfo";
import { IManagedSite, IManagedSiteCollection } from "../interfaces/IManagedSite";

const tryDecodeUriComponent = (uriComponent: string): string => {
  try {
    return decodeURIComponent(uriComponent)
  } catch (error) {
    // Ignore
  }
  return uriComponent;
}

// const encodeServerRelativePath = (serverRelativeUrl: string): string => {
//   let serverRelativeUrlParts = serverRelativeUrl.split("/");
//   for (let index = 0; index < serverRelativeUrlParts.length; index++) {
//     let urlPart = serverRelativeUrlParts[index];
//     urlPart = tryDecodeUriComponent(urlPart);
//     serverRelativeUrlParts[index] = tryEncodeUriComponent(urlPart);
//   }

//   return serverRelativeUrlParts.join("%2F").replaceAll("'", "''");
// }

const getSharePointSiteContent = async (
  accessToken: string,
  sharepointParameter: ISharePointParameters
): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${sharepointParameter.webUrl}/_api/web/lists(guid'${sharepointParameter.sharePointListId}')/${sharepointParameter.parameter}?${sharepointParameter.odataQuery}`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointSiteContent)");
    return [];
  }
};

export const getSharepointFiles = async (
  pager: IPager,
  accessToken: string,
  webUrl: string,
  driveId: string,
  sortOption: ISortOption,
  currentDirectory: IPageSettings,
  oneDriveRootDetails: IOneDriveRootDetails,
  context: IOfficeContextApp,
  filters?: FilterSection[]
): Promise<{ files: IFile[], nextPageUrl: string }> => {
  try {
    const isInOneDrive = (currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder);

    let folderServerRelativeUrl: string;
    try {
      if (currentDirectory.serverRelativePath) {
        folderServerRelativeUrl = tryDecodeUriComponent(currentDirectory?.serverRelativePath);
      }
      else if (currentDirectory?.isTeamsRootFolder && currentDirectory?.directory?.length > 0 && webUrl) {
        const resolvedPath = tryGetPathname(currentDirectory.directory, webUrl);
        if (resolvedPath) {
          folderServerRelativeUrl = tryDecodeUriComponent(resolvedPath);
        }
      }
      else if (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId) {
        let driveId = isInOneDrive ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);
        const itemWebUrl = await getListItemWebUrl(await authModule.getGraphAccessToken(), driveId, currentDirectory?.id);
        const resolvedPath = tryGetPathname(itemWebUrl, webUrl);
        if (resolvedPath) {
          folderServerRelativeUrl = tryDecodeUriComponent(resolvedPath);
        }
      }
    } catch (error) {
      logger(error, "SharePointRestService.ts (Try set folderServerRelativeUrl)");
    }

    let viewQuery: string;
    const baseUrl = `${webUrl}/_api/web/lists('${isInOneDrive ? oneDriveRootDetails.sharePointListId : currentDirectory.sharePointListId}')/RenderListDataAsStream`;
    let url: string;
    if (pager?.nextPageUrl) {
      url = `${baseUrl}${pager.nextPageUrl}`;
    } else {
      url = baseUrl + "?FilterField1=FSObjType&FilterValue1=0";

      if (Array.isArray(filters) && filters.length > 0) {
        let filterFieldCount = 2;
        const combinedQueryClauses: string[] = [];

        filters.forEach(section => {
          const sectionQueryConditions: string[] = [];

          if (section.daysOffset || section.daysOffset === 0) {
            let condition: string;
            if (section.daysOffset < 0) {
              // If the offset is negative, return dates from the past up to today
              condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Leq></And>`;
            } else if (section.daysOffset === 0) {
              // If the offset is zero, return results for today
              condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Eq>`;
            } else if (section.daysOffset === 1) {
              // If the offset is 1, return results only for tomorrow
              condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Eq>`;
            } else if (section.daysOffset > 1 && section.daysOffset <= 92) {
              // If the offset is between 1 and 92 (inclusive), return results from tomorrow up to the specified offset
              condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Leq></And>`;
            } else {
              // If the offset is 93 or higher, return results from tomorrow onwards
              condition = `<Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq>`;
            }
            sectionQueryConditions.push(condition);
          }
          else if (section.datasourceMutated) {
            section.items
              .filter(x => x.selected)
              .forEach(filterItem => {
                // getFieldWireType, not .Type: pre-select fallback sections carry
                // schema-origin fields whose type may live in FieldType only.
                sectionQueryConditions.push(`<Contains><FieldRef Name="${escapeXml(section.filterField.RealFieldName)}"/><Value Type="${escapeXml(getFieldWireType(section.filterField))}">${escapeXml(filterItem.value)}</Value></Contains>`);
              });
          }
          else if (section.items.some(x => x.selected)) {
            let selectedCount = section.items.filter(x => x.selected).length;
            let isWordSelected = section.items.some(x => x.selected && x.fileType === 'docx');
            let isExcelSelected = section.items.some(x => x.selected && x.fileType === 'xlsx');
            let isPowerPointSelected = section.items.some(x => x.selected && x.fileType === 'pptx');
            let isVisioSelected = section.items.some(x => x.selected && x.fileType === 'vsdx');

            // Per-value wire encoding (see encodeFilterValue). Items carrying a
            // fileType are DocIcon items: office buckets hold PRE-ENCODED
            // extension lists and plain extensions are URL-safe, so they keep the
            // legacy raw path (still guarded by the ";" / "&" fallbacks below).
            let fieldValues = section.items
              .filter(x => x.selected)
              .map(item => item.fileType ? item.value : encodeFilterValue(item.value, selectedCount > 1))
              .join('%3B%23');
            if (selectedCount > 1) {
              // Legacy ";;" escape — a no-op for encoded values (their ";" is
              // already "%3B"), retained for the raw DocIcon path.
              fieldValues = fieldValues.replaceAll(";", "%3B%3B");
            }

            const filterField = `FilterField${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.RealFieldName}`;
            const filterValue = `FilterValue${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${fieldValues}`;
            const filterType = `FilterType${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${getFieldWireType(section.filterField)}`;
            const filterOp = `FilterOp${filterFieldCount}=In`;

            url += `&${filterField}&${filterValue.replaceAll("&", "%26")}&${filterType}&${filterOp}`;

            filterFieldCount++;
          }

          const sectionQueryClause = buildLinearRecursiveCAML(sectionQueryConditions, "Or");
          if (sectionQueryClause) {
            combinedQueryClauses.push(sectionQueryClause);
          }
        });

        if (combinedQueryClauses.length > 0) {
          const whereClause = buildLinearRecursiveCAML(combinedQueryClauses, "And");
          if (whereClause) {
            viewQuery = `<Query><Where>${whereClause}</Where></Query>`;
          }
        }
      }
      else if (context) {
        url += `&FilterFields2=DocIcon&FilterValues2=${context.filter.values}&FilterTypes2=Computed&FilterOp2=In`;
      }

      url += `&SortField=${sortOption?.sortField || FileSortOptionKeys.SortFields.TimeLastModified}&SortDir=${sortOption?.sortOrder || FileSortOptionKeys.SortOrder.Descending}`;
    }

    let data = {
      parameters: {
        DatesInUtc: true,
        ViewXml: !viewQuery
          ? sharepoint.defaultViewXml
          : sharepoint.getDefaultFilteredViewXml(viewQuery)
      }
    }

    if (folderServerRelativeUrl) {
      data.parameters["FolderServerRelativeUrl"] = folderServerRelativeUrl;
    }

    let retry: boolean;
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    let response: AxiosResponse<any>;
    do {
      try {
        response = await axiosRetryPost(url, data, requestConfig, false);
        /**
        * If a sort option was specified without a next page href, it indicates that there should be data.
        * Retry with default sort options to ensure data is returned.
        */
        if (response?.data?.Row?.length === 0 && !pager?.nextPageUrl && sortOption?.sortField) {
          url = `${baseUrl}?FilterField1=FSObjType&FilterValue1=0&SortField=${FileSortOptionKeys.SortFields.TimeLastModified}&SortDir=${FileSortOptionKeys.SortOrder.Descending}`;
          retry = true;
        }
        else {
          break;
        }
      } catch (error) {
        // Retry throttling.
        if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860")) {
          data.parameters["ReplaceGroup"] = true;
          retry = true;
        }
        else {
          retry = false;
        }
      }
    } while (retry);

    const sharepointFiles: IFile[] = response?.data?.Row?.map(item => {
      let file: IFile = {
        id: item.UniqueId.replace('{', '').replace('}', ''),
        name: item.FileLeafRef,
        driveId: driveId,
        createdDateTime: item.Created,
        lastModifiedDateTime: item.Modified,
        modifiedBy: item.Editor[0]?.title,
        fileType: item.File_x0020_Type,
        size: item.File_x0020_Size,
        permMask: item.PermMask,
        renderItemID: item.ID,
        serverRelativePath: item.FileRef?.replace(`/${item.FileLeafRef}`, "")
      }

      const privacySensitive = item[piiConfig.privacySentitiveColumnName];
      if (privacySensitive !== undefined) {
        file.pii = {
          piiDetected: privacySensitive === "Yes" || privacySensitive === "True",
          pii: item[piiConfig.privacySentitiveDataColumnName]
        }
      }

      return file;
    });

    return {
      files: sharepointFiles ?? [],
      nextPageUrl: response?.data?.NextHref
    };

  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointFiles)");
    return {
      files: [],
      nextPageUrl: ''
    };
  }
};

export const getSharepointListSchema = async (accessToken: string, siteUrl: string, sharePointListId: string) => {
  try {
    const data = {
      parameters: {
        RenderOptions: 4
      }
    }
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    const response: AxiosResponse<any> = await axiosRetryPost(`${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`, data, requestConfig, false);
    return response?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharepointListSchema)");
    return null;
  }
};

/**
 * Definitive existence check for a single field/value: a RowLimit=1
 * RenderListDataAsStream filtered by ONLY `field = value` (plus the base
 * FSObjType=0 and the same folder scope). Returns true iff at least one row
 * matches — independent of list size / RenderListFilterData completeness. Used to
 * decide whether a pre-select value that was absent from the built value list is a
 * real (large-list truncated) value or genuinely non-existent. Fails open (returns
 * true) so a probe error never strikes a value.
 */
export const probeFilterValueExists = async (params: {
  siteUrl: string;
  listId: string;
  accessToken: string;
  realFieldName: string;
  filterType: string;
  value: string;
  serverRelativePath?: string;
}): Promise<boolean> => {
  try {
    const { siteUrl, listId, accessToken, realFieldName, filterType, value, serverRelativePath } = params;
    // Single-value clause, byte-compatible with the grid builder's own
    // single-value syntax (FilterField{n}/FilterValue{n}/FilterType{n} with
    // FilterOp=In — see getSharepointFiles above). The two MUST stay in
    // lockstep: probed values are always injected items (never DocIcon office
    // buckets, which are exempt from encoding in the grid builder), so both
    // sides encode through encodeFilterValue. If probe and grid ever encoded a
    // value differently, a strike decision could contradict the grid result.
    const encodedValue = encodeFilterValue(value);
    const url = `${siteUrl}/_api/web/lists('${listId}')/RenderListDataAsStream?FilterField1=FSObjType&FilterValue1=0&FilterField2=${realFieldName}&FilterValue2=${encodedValue}&FilterType2=${filterType}&FilterOp2=In`;
    const body: any = {
      parameters: {
        DatesInUtc: true,
        OverrideViewXml: "<View><RowLimit>1</RowLimit></View>",
        RenderOptions: 2
      }
    };
    if (serverRelativePath) {
      body.parameters.FolderServerRelativeUrl = tryDecodeUriComponent(serverRelativePath);
    }
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    const response: AxiosResponse<any> = await axiosRetryPost(url, body, requestConfig, false);
    const rows = response?.data?.Row ?? response?.data?.ListData?.Row;
    return Array.isArray(rows) && rows.length > 0;
  } catch (error) {
    logger(error, "SharePointRestService.ts (probeFilterValueExists)");
    return true; // fail-open: never strike a value on a probe failure
  }
};

export const getSharepointLibraryFiles = async (siteUrl: string, listTitle: string, groupProperty: string): Promise<NewDocumentTemplate[]> => {
  const templateFiles: NewDocumentTemplate[] = [];
  // Optional custom column on the template library whose value names the destination content type to
  // pre-select. The built-in "Content Type" field (reserved internal name "ContentType") is excluded:
  // it is a computed field that cannot be read via $select and would shadow a real custom column.
  const contentTypeColumnNames: string[] = ["contenttype", "content type"];
  const reservedContentTypeInternalName: string = "contenttype";
  try {
    let listGroupKeyInternalName: string;
    let contentTypeKeyInternalName: string;
    const listGroupProperty: string = groupProperty?.trim()?.toLowerCase();

    const listFields = await getFieldsByListTitle(await authModule.getSharePointAccessToken(), siteUrl, listTitle);
    if (Array.isArray(listFields) && listFields.length > 0) {
      if (listGroupProperty) {
        listGroupKeyInternalName = listFields.find(field => field && (field.Title?.toLowerCase()?.trim() === listGroupProperty || field.InternalName?.toLowerCase()?.trim() === listGroupProperty))?.InternalName;
      }

      contentTypeKeyInternalName = listFields.find(field => {
        const fieldInternalName: string = field?.InternalName?.toLowerCase()?.trim();
        const fieldTitle: string = field?.Title?.toLowerCase()?.trim();
        if (!fieldInternalName || fieldInternalName === reservedContentTypeInternalName) {
          return false;
        }
        return contentTypeColumnNames.includes(fieldTitle) || contentTypeColumnNames.includes(fieldInternalName);
      })?.InternalName;
    }

    // "Custom fields" column (scan-endpoint-managed envelope) - internal name first,
    // display name fallback; resolved from the same unconditional field fetch above.
    const customFieldsKeyInternalName = resolveCustomFieldsColumnInternalName(listFields);

    const selectFields: string[] = ["file/UniqueId", "file/Name", "file/ServerRelativeUrl"];
    if (listGroupKeyInternalName) {
      selectFields.push(listGroupKeyInternalName);
    }
    if (contentTypeKeyInternalName) {
      selectFields.push(contentTypeKeyInternalName);
    }
    if (customFieldsKeyInternalName) {
      selectFields.push(customFieldsKeyInternalName);
    }

    const groupKey: string = listGroupKeyInternalName?.trim()?.toLowerCase();
    const contentTypeKey: string = contentTypeKeyInternalName?.trim()?.toLowerCase();
    const customFieldsKey: string = customFieldsKeyInternalName?.trim()?.toLowerCase();

    // $top=5000 + nextLink paging: a bare /items request stops at SharePoint's 100-row default.
    // No $orderby/$filter - the default ID keyset order stays threshold-safe on large libraries,
    // folders are skipped client-side (no File) and the caller sorts by title.
    await forEachSharePointItemPage(`${siteUrl}/_api/web/lists/GetByTitle('${escapeODataString(listTitle)}')/items?$top=5000&$expand=file&$select=${selectFields.join(",")}`, templateResults => {
      for (const template of templateResults) {
        if (template.File) {
          let templateGroup: string | undefined;
          let templateContentTypeName: string | undefined;
          const templateProperties = Object.keys(template);

          if (groupKey) {
            const templateGroupPropKey: string | undefined = templateProperties.find(key => key.toLowerCase().trim() === groupKey);
            if (templateGroupPropKey) {
              templateGroup = template[templateGroupPropKey]?.trim();
            }
          }

          if (contentTypeKey) {
            const templateContentTypePropKey: string | undefined = templateProperties.find(key => key.toLowerCase().trim() === contentTypeKey);
            const contentTypeValue: any = templateContentTypePropKey ? template[templateContentTypePropKey] : undefined;
            if (typeof contentTypeValue === "string" && contentTypeValue.trim()) {
              templateContentTypeName = contentTypeValue.trim();
            }
          }

          let templateCustomFields: ICustomFieldDefinition[] | undefined;
          if (customFieldsKey) {
            const customFieldsPropKey: string | undefined = templateProperties.find(key => key.toLowerCase().trim() === customFieldsKey);
            if (customFieldsPropKey) {
              // Defensive parse: garbage/foreign/truncated JSON => undefined (treated as unscanned).
              templateCustomFields = parseCustomFieldsEnvelope(template[customFieldsPropKey]);
            }
          }

          templateFiles.push({
            templateId: template.File.UniqueId,
            title: removeExtension(template.File.Name),
            extention: getExtension(template.File.Name),
            url: template.File.ServerRelativeUrl,
            group: templateGroup,
            contentTypeName: templateContentTypeName,
            customFields: templateCustomFields
          });
        }
      }
    });
  } catch (err) {
    // A page that still fails after axiosRetryGet's retries keeps the pages already mapped.
    logger(err, "SharePointRestService.ts (getSharepointLibraryFiles)");
  }
  return templateFiles;
};

export const getSharepointListItems = async (siteUrl: string, listTitle: string, groupProperty: string): Promise<IManagedSiteCollection> => {
  const groups: Set<string> = new Set<string>();
  const sites: IManagedSite[] = [];

  const listGroupProperty: string = groupProperty?.trim()?.toLowerCase();
  const siteUrlKeys: string[] = ["siteurl", "site url", "url"];
  const siteIdKeys: string[] = ["siteid", "site id", "clientsiteid"];

  let listGroupKey: string;
  if (listGroupProperty) {
    const listFields = await getFieldsByListTitle(await authModule.getSharePointAccessToken(), siteUrl, listTitle);
    if (Array.isArray(listFields) && listFields.length > 0) {
      listGroupKey = listFields.find(field => field && (field.Title?.toLowerCase()?.trim() === listGroupProperty || field.InternalName?.toLowerCase()?.trim() === listGroupProperty))?.InternalName?.toLowerCase().trim();
    }
  }

  let nextLink: string = `${siteUrl}/_api/web/lists/GetByTitle('${listTitle}')/items?$top=5000`;

  try {
    while (nextLink) {
      const requestConfig = {
        headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, Accept: "application/json;odata=nometadata" }
      };
      const response: AxiosResponse<any> = await axiosRetryGet(nextLink, requestConfig);
      const itemsResult: any[] = response?.data?.value?.filter(item => item);
      if (Array.isArray(itemsResult) && itemsResult.length > 0) {
        for (const item of itemsResult) {
          const itemProperties = Object.keys(item);
          let siteGroupPropKey: string | undefined = listGroupKey && itemProperties.find(key => key.toLowerCase().trim() === listGroupKey);
          let siteUrlPropKey: string | undefined = itemProperties.find(key => siteUrlKeys.some(siteUrlKey => key.toLowerCase().trim() === siteUrlKey));
          let siteIdPropKey: string | undefined = itemProperties.find(key => siteIdKeys.some(siteIdKey => key.toLowerCase().trim() === siteIdKey));
          if (siteUrlPropKey || siteIdPropKey) {
            const group: string = siteGroupPropKey ? item[siteGroupPropKey]?.trim() : undefined;
            if (group) {
              groups.add(group);
            }

            sites.push({
              siteGroup: group,
              siteUrl: siteUrlPropKey ? (item[siteUrlPropKey]?.Url || item[siteUrlPropKey])?.toLowerCase()?.trim() : undefined,
              siteId: siteIdPropKey ? item[siteIdPropKey]?.toLowerCase()?.trim() : undefined
            });
          }
        }
      }
      nextLink = response?.data
        ? response.data["odata.nextLink"]
        : undefined;
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharepointListItems)");
  }
  return { groups, sites };
};

export const getFilePreCommitInfo = async (request: IFilePreCommitRequest): Promise<IFilePreCommitResult> => {
  try {
    return await apiFilePreCommitCheck(request);
  } catch {
    return {
      exists: false,
      checkedOut: false,
      checkedOutBy: "",
      locked: false,
      lockedBy: ""
    };
  }
};

export const getFolderPreCommitInfo = async (request: FolderPreCommitRequest): Promise<IFilePreCommitResult> => {
  try {
    return await apiFolderPreCommitCheck(request);
  } catch {
    return {
      exists: false,
      checkedOut: false,
      checkedOutBy: "",
      locked: false,
      lockedBy: ""
    };
  }
};

export const getSharePointGroupUsers = async (
  accessToken: string,
  webUrl: string,
  userSelectionScope: string
): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/Web/SiteGroups/GetById(${userSelectionScope})/users?$select=Id,Title`,
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointGroupUsers)");
    return [];
  }
};

const getSharePointSiteUsersCall = async (accessToken: string, webUrl: string): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/siteusers?$select=Id,Title,Email,UserPrincipalName&$filter=(Email ne '' or (UserPrincipalName ne '' and UserPrincipalName ne null))`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointSiteUsersCall)");
    return [];
  }
};

export const getSharePointContentTypes = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  includeParentData: boolean = false
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: "contentTypes",
    odataQuery: !includeParentData ? "$select=id,name,sealed" : "$select=id,name,sealed,parent/id&$expand=parent/id"
  });

export const getSharePointContentTypesWithFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: "contentTypes",
    odataQuery: "$select=id,name,sealed,fields/Group,fields/Hidden,fields/InternalName,fields/Required&$expand=fields"
  });

export const getSharePointContentTypeFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  contentTypeId: string,
  query: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: contentTypeId ? `contentTypes('${contentTypeId}')/fields` : "fields",
    odataQuery: query
  });

export const getSharePointDocumentLibraryFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  query: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: `/fields`,
    odataQuery: query
  });

export const getSharePointSiteUsers = async (accessToken: string, webUrl: string): Promise<any[]> => {
  return await getSharePointSiteUsersCall(accessToken, webUrl);
};

export const getSharePointSiteUsersById = async (webUrl: string, idList: number[]): Promise<ISiteUser[]> => {
  // Normalize defensively: a nested array or comma-joined string in idList would
  // otherwise produce an invalid filter like "(Id eq 33,32,30)".
  const ids: number[] = normalizeUserIdList(idList);
  if (ids.length > 0) {
    try {
      const filter: string = ids.map(id => `(Id eq ${id})`).join(" or ");
      const response = (await axiosRetryGet(`${webUrl}/_api/web/siteuserinfolist/items?$select=Id,Title,EMail,UserName&$filter=${filter}`,
        {
          headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
        }));

      if (Array.isArray(response?.data?.value)) {
        return response.data.value;
      }
    } catch (err) {
      logger(err, "SharePointRestService.ts (getSharePointSiteUsersById)");
    }
  }
  return [];
}

export const searchSharePointSiteUsers = async (webUrl: string, filterValue: string, allowGroups: boolean): Promise<ISiteUser[]> => {
  try {
    filterValue = filterValue.replaceAll("'", "''");
    const response = (await axiosRetryGet(`${webUrl}/_api/web/siteuserinfolist/items?$select=Id,Title,EMail,UserName,Name&$filter=(substringof('${filterValue}',Title) or substringof('${filterValue}',UserName) or substringof('${filterValue}',EMail)) and (EMail ne null or UserName ne null)`,
      {
        headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
      }));

    if (Array.isArray(response?.data?.value)) {
      if (!allowGroups) {
        return response.data.value.filter(x => x.Name?.toLowerCase()?.indexOf("|membership|") !== -1);
      }
      return response.data.value;
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (searchSharePointSiteUsers)");
  }
  return [];
}

export const searchSharePointGroup = async (webUrl: string, groupToSearch: string, allowGroups: boolean, filterValue: string): Promise<ISiteUser[]> => {
  /*PrincipalSource - The principal sources to search:
 
    All (15) - Search all principal sources.
    MembershipProvider (4) - Search the current membership provider.
    None (0) - Search no principal sources.
    RoleProvider (8) - Search the current role provider.
    UserInfoList (1) - Search the user information list.
    Windows (2) - Search active directory.
 
    PrincipalType - The principal types to return:
 
    All (15) - Return all entity types.
    DistributionList (2) - Return distribution list entity types.
    None (0) - Return no principal types.
    SecurityGroup (4) - Return security group entity types.
    SharePointGroup (8) - Return sharepoint group entity types.
    User (1) - Return user entity types.
*/
  try {
    const response = (await axiosRetryPost(`${webUrl}/_api/SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.ClientPeoplePickerSearchUser`,
      {
        queryParams: {
          QueryString: filterValue,
          MaximumEntitySuggestions: 50,
          AllowEmailAddresses: false,
          AllowOnlyEmailAddresses: false,
          PrincipalType: allowGroups ? 13 : 1,
          PrincipalSource: 15,
          SharePointGroupID: Number.parseInt(groupToSearch)
        }
      },
      {
        headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json;odata=verbose" }
      }));

    if (response?.data?.d?.ClientPeoplePickerSearchUser) {
      const jsonResult = JSON.parse(response.data.d.ClientPeoplePickerSearchUser);
      if (Array.isArray(jsonResult)) {
        return jsonResult.map(x => ({
          Id: -1,
          Title: x.DisplayText ?? x.EntityData?.DisplayName,
          EMail: x.EntityData?.Email,
          LogonName: x.Key
        }));
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (searchSharePointSiteUsers)");
  }
  return [];
}

export const getTaxonomyTerms = async (accessToken: string, termSetId: string, webUrl: string): Promise<ITag[]> => {
  try {
    const data = `<Request xmlns="http://schemas.microsoft.com/sharepoint/clientquery/2009" SchemaVersion="15.0.0.0" LibraryVersion="16.0.0.0" ApplicationName="PnPjs"><Actions><ObjectPath Id="1" ObjectPathId="0" /><ObjectPath Id="3" ObjectPathId="2" /><Query Id="4" ObjectPathId="2"><Query SelectAllProperties="true" ><Properties /></Query ></Query ><ObjectPath Id="6" ObjectPathId="5" /><ObjectPath Id="8" ObjectPathId="7" /><Query Id="9" ObjectPathId="7"><Query SelectAllProperties="true" ><Properties /></Query ><ChildItemQuery SelectAllProperties="false" ><Properties><Property Name="Id" SelectAll="true" /><Property Name="Name" SelectAll="true" /><Property Name="IsDeprecated" SelectAll="true" /><Property Name="IsAvailableForTagging" SelectAll="true" /><Property Name="PathOfTerm" SelectAll="true" /><Property Name="TermsCount" SelectAll="true" /><Property Name="Parent" SelectAll="true" /><Property Name="Labels" SelectAll="true" /><Property Name="TermSet" SelectAll="true" /></Properties></ChildItemQuery ></Query ></Actions><ObjectPaths><StaticMethod Id="0" Name="GetTaxonomySession" TypeId="{981cbc68-9edc-4f8d-872f-71146fcbb84f}" /><Method Id="2" ParentId="0" Name="GetDefaultSiteCollectionTermStore"></Method><Method Id="5" ParentId="2" Name="GetTermSet"><Parameters><Parameter Type="String">${termSetId}</Parameter></Parameters></Method><Method Id="7" ParentId="5" Name="GetAllTerms"></Method></ObjectPaths></Request>`;
    let response = await axiosRetryPost(`${webUrl}/_vti_bin/client.svc/ProcessQuery?`, data, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response.data
      .filter(item => item._ObjectType_ === sharepointType.taxonomyTerm)
      .reduce((agg, cur) => [...agg, ...cur._Child_Items_], [])
      .map(item => {
        return {
          key: item.Id.replace("/Guid(", "").replace(")/", ""),
          name: item.Name
        };
      });
  } catch (err) {
    logger(err, "SharePointRestService.ts (getTaxonomyTerms)");
    return [];
  }
};

export const getSharePointDocumentSetSettingsXml = async (
  accessToken: string,
  webUrl: string,
  sharePointListId: string,
  contentTypeId: string,
): Promise<any> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${sharePointListId}')/contentTypes('${contentTypeId}')?$select=SchemaXml`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointDocumentSetSettingsXml)");
    return undefined;
  }
};

export const getSharePointListViews = async (accessToken: string, webUrl: string, sharePointListId: string): Promise<IViewInfo[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${sharePointListId}')/views?$select=Id,Title,DefaultView,ListViewXml,ViewType2&$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointListViews)");
    return undefined;
  }
};

export const getUserEffectivePermissions = async (accessToken: string, webUrl: string, sharePointListId?: string, sharePointUniqueId?: string, username?: string): Promise<currentSharepointFolderUserPermissions> => {
  try {
    const encodedUsername: string = username && tryEncodeUriComponent(`i:0#.f|membership|${username.replaceAll("'", "''")}`);
    const baseUrl: string = !sharePointListId
      ? `${webUrl}/_api/web`
      : (!sharePointUniqueId
        ? `${webUrl}/_api/web/lists('${sharePointListId}')`
        : `${webUrl}/_api/web/lists('${sharePointListId}')/GetItemByUniqueId('${sharePointUniqueId}')`);

    let url: string = !encodedUsername
      ? `${baseUrl}/EffectiveBasePermissions`
      : `${baseUrl}/getUserEffectivePermissions('${encodedUsername}')`;

    let retryLimit: number = 3;

    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    let perms: IBasePermissions;

    while (retryLimit > 0) {
      try {
        perms = (await axiosRetryGet(url, requestConfig)).data;
        break;
      } catch (error) {
        // Retry intermittent getUserEffectivePermissions() endpoint.
        if (!encodedUsername || error?.response?.status !== 403) {
          throw error;
        }
      }

      if (--retryLimit === 0 && encodedUsername) {
        // Last retry also failed, retry one last time with default endpoint
        perms = (await axiosRetryGet(`${baseUrl}/EffectiveBasePermissions`, requestConfig)).data;
      }
    }

    if (perms) {
      return getPermissionsFromBase(perms);
    }
  } catch (err) {
    logger(
      err,
      `SharePointRestServices.ts (getUserEffectivePermissions listId=${Boolean(sharePointListId)} uniqueId=${Boolean(sharePointUniqueId)} username=${Boolean(username)} oneDriveUrl=${webUrl?.includes("/personal/") === true})`
    );
  }
  return null;
};

export const getUserEffectivePermissionsFromMask = (permHex: string): currentSharepointFolderUserPermissions => {
  if (permHex) {
    try {
      const basePerm: IBasePermissions = {
        Low: getPermMaskL(permHex),
        High: getPermMaskH(permHex)
      };
      return getPermissionsFromBase(basePerm);
    } catch (error) {
      logger(error, "SharePointRestService.ts (getUserEffectivePermissionsFromMask)");
    }
  }
  return null;
}

export const getLists = async (accessToken: string, webUrl: string): Promise<IListInfo[]> => {
  try {
    const lists = (await axiosRetryGet(`${webUrl}/_api/web/lists?$select=Id,ItemCount&$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    return Array.isArray(lists?.value) && lists.value.length > 0
      ? lists.value.map(list => ({ Id: list.Id, ItemCount: list.ItemCount }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getLists)");
    return null;
  }
};

export const getListSettings = async (accessToken: string, webUrl: string, sharePointListId: string): Promise<IListInfo> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists('${sharePointListId}')?$select=EnableFolderCreation,ContentTypesEnabled,EnableMinorVersions,ExcludeFromOfflineClient,ForceCheckout,ItemCount`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListSettings)");
    return null;
  }
};

export const createLink = async (accessToken: string, webUrl: string, serverRelativePath: string, linkName: string, payload: any, overwrite: boolean = false): Promise<any> => {
  try {
    const url = `${webUrl}/_api/Web/GetFolderByServerRelativePath(decodedurl='${encodePath(tryDecodeUriComponent(serverRelativePath))}')/Files/AddUsingPath(decodedUrl='${encodePath(tryDecodeUriComponent(linkName))}',overwrite=${overwrite === true})`
    return (await axiosRetryPost(url, payload,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      })).data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (createLink)");
    throw (error);
  }
}

export const getShortcutLink = async (accessToken: string, webUrl: string, fileUrl: string): Promise<any> => {
  try {
    const url = `${webUrl}/_api/SP.Utilities.ShortcutLink.GetShortcutLink(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    return (await axiosRetryGet(url,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      }))?.data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (createLink)");
  }
  return null;
}

/**
 * SharingLinkKind — the "kind" of tokenized sharing link to create.
 * Maps to SP.SharingLinkKind ([MS-CSOMSPT] SharingLinkData.LinkKind).
 *
 * For NEW links always use `Flexible` (6): it honours `scope` + `role` and supports
 * expiration, password and block-download. The legacy kinds (2–5) hard-code both the
 * audience and the permission and IGNORE `role`, so they cannot express e.g.
 * "view + block download".
 */
export enum SharingLinkKind {
  /** 0 — Not initialised. Never send this. */
  Uninitialized = 0,
  /** 1 — Direct link: grants access only to people already on the item (no new tokenised link). */
  Direct = 1,
  /** 2 — Legacy "people in your organization can VIEW" link. Ignores `role`. */
  OrganizationView = 2,
  /** 3 — Legacy "people in your organization can EDIT" link. Ignores `role`. */
  OrganizationEdit = 3,
  /** 4 — Legacy "anyone with the link can VIEW" (anonymous) link. Ignores `role`. */
  AnonymousView = 4,
  /** 5 — Legacy "anyone with the link can EDIT" (anonymous) link. Ignores `role`. */
  AnonymousEdit = 5,
  /** 6 — Modern flexible link: audience from `scope`, permission from `role`; supports
   *      expiration / password / block-download. Preferred for all new links. */
  Flexible = 6
}

/**
 * SharingRole — the permission the link grants. Maps to SP.Sharing.Role.
 * Only honoured when `linkKind === Flexible`. Values above `Owner` are newer and may
 * not be available on every tenant; SharePoint falls back to the nearest supported role.
 */
export enum SharingRole {
  /** 0 — No access. */
  None = 0,
  /** 1 — Can view (read only). */
  View = 1,
  /** 2 — Can edit (read / write). */
  Edit = 2,
  /** 3 — Owner / full control. Not used for ordinary sharing links. */
  Owner = 3,
  /** 4 — Limited view (open in browser only; used by app/embed scenarios). */
  LimitedView = 4,
  /** 5 — Limited edit. */
  LimitedEdit = 5,
  /** 6 — Review (can comment/review, cannot change content). */
  Review = 6,
  /** 7 — Restricted view: can view online but CANNOT download / print / copy
   *      (this is the "block download" link). Requires a tenant that supports
   *      block-download links; otherwise SharePoint treats it as plain View. */
  RestrictedView = 7
}

/**
 * SharingLinkScope — the audience of a Flexible link (who the link works for).
 * Maps to the `scope` member of SharingLinkData.
 */
export enum SharingLinkScope {
  /** 0 — Anyone with the link (anonymous). Requires the tenant/site external-sharing
   *      capability to allow "Anyone" links, otherwise the request fails. */
  Anyone = 0,
  /** 1 — Only people inside the organization (tenant). */
  Organization = 1,
  /** 2 — Only specific people. The recipients MUST be supplied via `peoplePickerInput`. */
  SpecificPeople = 2
}

/**
 * ISharingLinkSettings → the `settings` object of the ShareLink request
 * (SP.Sharing.SharingLinkData). Only `linkKind` is strictly required; for a Flexible
 * link also set `scope` + `role`. All other members are optional and default to "off".
 */
export interface ISharingLinkSettings {
  /** Which kind of link to create. Use SharingLinkKind.Flexible for new links. */
  linkKind: SharingLinkKind;
  /** Audience for Flexible links. Ignored for the legacy OrganizationView/Edit and AnonymousView/Edit kinds. */
  scope?: SharingLinkScope;
  /** Permission granted. Required for Flexible links; ignored for the legacy kinds. */
  role?: SharingRole;
  /** ISO-8601 UTC expiry, e.g. "2026-12-31T23:59:59Z". null / omitted = never expires
   *  (anonymous links may still be force-expired by tenant policy). */
  expiration?: string | null;
  /** true to allow anonymous ("Anyone") access. Must be true for scope = Anyone. */
  allowAnonymousAccess?: boolean;
  /** true LOCKS the link to the people explicitly granted access via `peoplePickerInput` — the
   *  defining property of a "Specific people" link (pair it with scope = SpecificPeople). The
   *  link then cannot be redeemed by anyone else it is forwarded to. false leaves the link open
   *  to its whole `scope` audience (the correct value for Organization / Anyone links). This is
   *  the same value SharePoint's own Share dialog sends: true for specific-people, false otherwise. */
  restrictShareMembership?: boolean;
  /** Password to protect the link (only valid for anonymous links where the tenant allows it). */
  password?: string;
  /** Set true together with `password` to set/change/clear the link password. */
  updatePassword?: boolean;
  /** Internal app-link flags — leave false/omitted for ordinary user-facing sharing. */
  applicationLink?: boolean;
  limitUseToApplication?: boolean;
}

/**
 * Creates a tokenized SharePoint sharing link via the REST `ShareLink` method
 * (SP.Sharing — [MS-CSOMSPT] §ShareLink), the server-side primitive behind the
 * "Copy link / Share" dialog. Intended as a FALLBACK when the Microsoft Graph
 * `/drives/{id}/items/{id}/createLink` endpoint is unavailable or returns no URL.
 *
 * Endpoint (POST):
 *   {webUrl}/_api/web/Lists('{listId}')/GetItemByUniqueId('{itemUniqueId}')/ShareLink
 * (GetItemByUniqueId is used instead of GetItemById so we can address the item by its
 *  GUID UniqueId, which Docubird already has, rather than the per-list integer Id.)
 *
 * Request body — SP.Sharing.ShareLinkRequest:
 *   { request: { createLink: true, settings: <SharingLinkData>, peoplePickerInput?: <json> } }
 *   - createLink:true       → mint a NEW link (vs. only returning an existing one).
 *   - settings              → the ISharingLinkSettings above (linkKind / scope / role / …).
 *   - peoplePickerInput     → for scope = SpecificPeople: a JSON string of the target
 *                             principals, each as { Key, DisplayText }. `Key` may be a plain
 *                             email (resolves for guests/most users) or the login-name claims
 *                             format `i:0#.f|membership|<email>` (needed by some tenants for
 *                             internal users). Single attempt — the caller picks the encoding
 *                             and may retry with the other if this returns null.
 *
 * Response — SP.Sharing.ShareLinkResponse: { sharingLinkInfo: { Url, IsEditLink, LinkKind, … } };
 * with odata=verbose it is wrapped as d.ShareLink.sharingLinkInfo.Url.
 *
 * Permissions required:
 *   - Token: a SharePoint REST token carrying the `AllSites.Manage` delegated scope
 *     (authModule.getSharePointAccessToken / getOneDriveAccessToken). It acts AS the
 *     signed-in user — it does not elevate privileges.
 *   - The signed-in user must be allowed to share the item: Full Control, OR
 *     Edit/Contribute while the site's "members can share" setting is enabled. An edit
 *     link additionally requires the caller to hold edit rights on the item.
 *   - Anonymous ("Anyone") links additionally require the tenant/site external-sharing
 *     capability to permit them, regardless of the user's own rights.
 *
 * @returns the sharing link URL, or null on any failure (caller decides how to surface it).
 */
export const createSharingLinkViaRest = async (
  accessToken: string,
  webUrl: string,
  listId: string,
  itemUniqueId: string,
  settings: ISharingLinkSettings,
  peoplePickerInput?: string
): Promise<string | null> => {
  try {
    const shareLink = buildShareLinkRequest(webUrl, listId, itemUniqueId, settings, peoplePickerInput);
    if (!shareLink) {
      return null;
    }

    // retryInternalServerError = false: a 500 from ShareLink is almost always a real
    // policy/permission error that will not succeed on retry (429 throttling is still retried).
    const response = await axiosRetryPost(shareLink.url, shareLink.body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accept: "application/json;odata=verbose",
        "content-type": "application/json;odata=verbose"
      }
    }, false);

    // `|| null` (not `?? null`): treat an empty/blank Url as failure so the caller's
    // claims-format retry / error path triggers instead of propagating a useless "".
    return readSharingLinkUrl(response?.data) || null;
  } catch (error) {
    // Keep SharePoint's own explanation: axios's message alone is "Request failed with status code 400".
    logger(error, "SharePointRestService.ts (createSharingLinkViaRest)", readSharePointErrorMessage(error));
    return null;
  }
};

/**
 * Reads a list item's sharing info via GetSharingInformation (SP.Sharing.SharingInformation) in a
 * SINGLE call, returning both (a) the "shared with" principals for the info tooltip and (b) the
 * external-sharing capability reduced to two booleans that map to the site/tenant "External file
 * sharing" (SharingCapability) setting:
 *
 *   "External file sharing"            canShareExternally   canInviteNewExternal
 *   --------------------------------   ------------------   --------------------
 *   Only people in your organization   false                false
 *   Existing guests                    true                 false
 *   New and existing guests            true                 true
 *   Anyone                             true                 true   (we never offer anonymous links)
 *
 * IMPORTANT — verified against a live "Existing guests" response, the field names are misleading:
 *   - TOP-LEVEL `canAddExternalPrincipal` actually means "can add a NEW external principal" — it is
 *     FALSE for "Existing guests" even though existing guests can still be added. Do NOT use it as the
 *     "external allowed at all" signal.
 *   - TOP-LEVEL `canAddExistingExternalPrincipal` means "can add an EXISTING external principal".
 *   - The cleanest source for the panel's "specific people" LINK flow is the nested
 *     `sharingAbilities.peopleSharingLinkAbilities`: `canHaveExternalUsers.enabled` (external allowed at
 *     all) and `canAddNewExternalPrincipals.enabled` (new external allowed). Each ability is an object
 *     of shape { disabledReason, enabled }. Note people-link uses the PLURAL `…Principals`, while
 *     `directSharingAbilities` uses the SINGULAR `canAddNewExternalPrincipal`.
 *
 * We request WITH `$expand=permissionsInformation,pickerSettings,sharingAbilities` for the precise
 * people-link signals, falling back to `$expand=permissionsInformation,pickerSettings` if the
 * sharingAbilities expand is rejected — the tooltip principals still resolve and the capability flags
 * fall back to the always-present top-level fields. Every signal is read from several candidate
 * locations/casings, returning `undefined` for anything we cannot determine so callers FAIL OPEN.
 *
 * Permission: the SharePoint REST token (`AllSites.Manage`), acting as the signed-in user.
 */
export const getItemSharingInfo = async (
  accessToken: string,
  webUrl: string,
  listId: string,
  itemUniqueId: string
): Promise<{ canShareExternally?: boolean; canInviteNewExternal?: boolean; principals?: any[] }> => {
  const normalizedListId: string = listId?.replace("{", "").replace("}", "");
  const normalizedUniqueId: string = itemUniqueId?.replace("{", "").replace("}", "");
  if (!webUrl || !normalizedListId || !normalizedUniqueId) {
    return {};
  }

  // Each nested "ability" is { disabledReason, enabled }; pull its boolean `enabled`.
  const enabledOf = (ability: any): boolean | undefined =>
    ability && typeof ability.enabled === "boolean" ? ability.enabled : undefined;
  const asBool = (value: any): boolean | undefined => (typeof value === "boolean" ? value : undefined);
  const firstBool = (...candidates: (boolean | undefined)[]): boolean | undefined => {
    for (const candidate of candidates) {
      if (typeof candidate === "boolean") return candidate;
    }
    return undefined;
  };

  const parse = (data: any): { canShareExternally?: boolean; canInviteNewExternal?: boolean; principals?: any[] } => {
    // odata=verbose wraps the result under `d`; minimal/nometadata returns it at the root.
    const info = data?.d ?? data ?? {};
    const abilities = info.sharingAbilities ?? info.SharingAbilities ?? {};
    const peopleLink = abilities.peopleSharingLinkAbilities ?? abilities.PeopleSharingLinkAbilities;
    const direct = abilities.directSharingAbilities ?? abilities.DirectSharingAbilities;

    // Can the share include ANY external recipient (i.e. an existing guest)?
    const canShareExternally = firstBool(
      enabledOf(peopleLink?.canHaveExternalUsers),
      asBool(info.canAddExistingExternalPrincipal),
      enabledOf(direct?.canAddExternalPrincipal)
    );

    // Can the share invite a NEW external recipient (one not already a guest)?
    const canInviteNewExternal = firstBool(
      enabledOf(peopleLink?.canAddNewExternalPrincipals),  // people-link: PLURAL
      asBool(info.canAddExternalPrincipal),                // top-level: actually the "new external" flag
      enabledOf(direct?.canAddNewExternalPrincipal)        // direct: SINGULAR
    );

    // "Shared with" principals for the tooltip. A recipient can have access via EITHER a direct permission
    // grant (permissionsInformation.principals) OR — for the modern "Specific people" / org sharing LINKS,
    // the default sharing model — only as a member of a link (permissionsInformation.links[].linkMembers).
    // Reading principals alone drops every link-shared guest, so merge both sources (see mergeSharedWithPrincipals).
    const permissions = info.permissionsInformation ?? info.PermissionsInformation;
    const mergedPrincipals = mergeSharedWithPrincipals(permissions);
    const principals: any[] | undefined = mergedPrincipals.length > 0 ? mergedPrincipals : undefined;

    return { canShareExternally, canInviteNewExternal, principals };
  };

  const requestConfig = {
    headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json" }
  };
  const baseUrl = `${webUrl}/_api/web/lists('${normalizedListId}')/GetItemByUniqueId('${normalizedUniqueId}')/GetSharingInformation`;

  try {
    // ONE call serving BOTH the external-sharing capability (sharingAbilities) and the "shared with"
    // tooltip (permissionsInformation). Prefer the full expand for the precise people-link signals.
    const response = await axiosRetryPost(`${baseUrl}?$expand=permissionsInformation,pickerSettings,sharingAbilities`, null, requestConfig, false);
    return parse(response?.data);
  } catch (error) {
    // sharingAbilities may be unsupported — retry with the original expand only. The tooltip principals
    // still resolve, and the capability flags fall back to the always-present top-level fields.
    try {
      const response = await axiosRetryPost(`${baseUrl}?$expand=permissionsInformation,pickerSettings`, null, requestConfig, false);
      return parse(response?.data);
    } catch (err) {
      logger(err, "SharePointRestService.ts (getItemSharingInfo)");
      return {};
    }
  }
};

export const getFileByUrl = async (accessToken: string, webUrl: string, fileUrl: string): Promise<any | null> => {
  try {
    const webHost: string = new URL(webUrl).hostname.toLowerCase().trim();
    const fileHost: string = new URL(fileUrl).hostname.toLowerCase().trim();

    let requestUrl: string;
    if (webHost === fileHost) {
      requestUrl = `${webUrl}/_api/web/GetFileByUrl(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    }
    else {
      requestUrl = `${webUrl}/_api/SP.RemoteWeb(@url)/web/GetFileByUrl(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    }

    return (await axiosRetryGet(requestUrl,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      })).data;
  } catch (error) {
    logger(error, "SharePointRestService.ts (getFileByUrl)");
  }
  return null;
}

export const getListSharingInformation = async (accessToken: string, webUrl: string, listId: string): Promise<any> => {
  try {
    const url = `${webUrl}/_api/web/Lists('${listId}')`;
    return (await axiosRetryPost(url, {},
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      }))?.data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (getListSharingInformation)");
  }
  return null;
}

export const getLibraryDefaultValues = async (accessToken: string, webUrl: string, directory: string): Promise<ILibraryDefault[]> => {
  if (webUrl && directory) {
    try {
      const directoryParts: string[] = directory.split("/")?.filter(v => v);
      if (Array.isArray(directoryParts) && directoryParts.length > 0) {
        const domain = new URL(webUrl).origin;
        const libraryUrl = `${webUrl.replace(domain, '')}/${directoryParts[0]}`;

        let xmlData: ILibraryDefault[] = CacheHelper.get(libraryUrl);

        if (!Array.isArray(xmlData)) {
          xmlData = [];
          try {
            const url = `${webUrl}/_api/Web/GetFileByServerRelativePath(decodedurl='${encodePath(tryDecodeUriComponent(libraryUrl))}/Forms/client_LocationBasedDefaults.html')/$value?binaryStringResponseBody=true`
            const xmlString = (await axiosRetryGet(url,
              {
                headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
              })).data;

            parseString(xmlString, (err, result) => {
              if (result && result.MetadataDefaults?.a) {
                xmlData = result.MetadataDefaults?.a?.map((x: any) => {
                  return {
                    folder: tryDecodeUriComponent(x.$.href),
                    values: x.DefaultValue.map((defaultValue: any) => {
                      return {
                        fieldName: defaultValue.$.FieldName,
                        value: defaultValue._
                      };
                    })
                  };
                });
              }
            });
          } catch (error) {
            // Assume no default vaues found
          }
          CacheHelper.set(libraryUrl, xmlData);
        }

        return xmlData;
      }
    } catch (err) {
      logger(err, "SharePointRestService.ts (getLibraryDefaultValues)");
    }
  }
  return null;
};

// export const getSiteTimeZoneOffset = async (accessToken: string, webUrl: string): Promise<number | null> => {
//   try {
//     const timeZone = (await axiosRetryGet(`${webUrl}/_api/web/regionalsettings/TimeZone?$select=Id,Information`,
//       {
//         headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
//       })).data;

//     if (timeZone) {
//       const timeZoneInfo = timeZone.Information;
//       // https://github.com/SharePoint/sp-dev-docs/issues/6083
//       // var offset = -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 -1;
//       // return dayjs.utc(value).utcOffset(offset);
//       // return -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 - 1;
//       return -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 - (GetIsTimeZoneIdInDST(timeZone.Id.toString()) ? 1 : 0);
//     }
//   } catch (err) {
//     logger(err, "SharePointRestService.ts (getSiteTimeZoneOffset)");
//   }
//   return null;
// };

export const getSiteLocaleInfo = async (accessToken: string, webUrl: string): Promise<ISiteLocaleInfo | null> => {
  try {
    const localeInfo = (await axiosRetryGet(`${webUrl}/_api/web/regionalsettings?$expand=TimeZone&$select=LocaleId,TimeZone/Id`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    if (localeInfo && Number(localeInfo.LocaleId) > 0 && Number(localeInfo.TimeZone?.Id) > 0) {
      return {
        locale: lcid.from(localeInfo.LocaleId).slice(0, 2),
        timeZoneName: GetIANATimeZoneName(localeInfo.TimeZone.Id.toString())
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSiteLocaleInfo)");
  }
  return null;
};

export const getListItemsById = async (accessToken: string, webUrl: string, sharePointListId: string, columnInternalName: string, ids: number[]): Promise<ITag[]> => {
  // Normalize defensively: a nested array or comma-joined string in ids would
  // otherwise produce an invalid filter like "Id eq 1,2,3".
  const idList: number[] = normalizeUserIdList(ids);
  if (idList.length === 0) return [];
  try {
    const showField: string = columnInternalName || "Title";
    const items = (await axiosRetryGet(`${webUrl}/_api/web/lists('${sharePointListId}')/items?$select=Id,${showField}&$filter=${idList.map(id => `Id eq ${id}`).join(" or ")}`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    return Array.isArray(items?.value) && items.value.length > 0
      ? items.value.map(item => ({ key: item.Id, name: item[showField] }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListItemsById)");
    return null;
  }
};

export const discardCheckOut = async (accessToken: string, webUrl: string, itemUniqueId: string): Promise<void> => {
  try {
    await axiosRetryPost(`${webUrl}/_api/web/GetFileById(guid'${itemUniqueId}')/undocheckout`,
      null,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }, false);
  } catch (err) {
    logger(err, "SharePointRestService.ts (discardCheckOut)");
  }
};

export const getItemAbsoluteUrl = async (accessToken: string, webUrl: string, listUniqueId: string, itemUniqueId: string): Promise<string> => {
  let absUrl: string;
  try {
    absUrl = (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/GetItemByUniqueId('${itemUniqueId}')?$select=EncodedAbsUrl`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data?.EncodedAbsUrl;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getItemAbsoluteUrl)");
  }
  return absUrl;
};

export const getItemVersions = async (accessToken: string, webUrl: string, listUniqueId: string, itemUniqueId: string): Promise<any> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/GetItemByUniqueId('${itemUniqueId}')/Versions`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getItemVersions)");
  }
  return null;
};

export const getListFields = async (accessToken: string, webUrl: string, listUniqueId: string): Promise<any> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/fields?$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListFields)");
  }
  return null;
};

export const getFieldsByListTitle = async (accessToken: string, webUrl: string, listTitle: string): Promise<{ InternalName: string, Title: string, TypeAsString?: string }[]> =>
  getFieldsByListEndpoint(accessToken, webUrl, `lists/getbytitle('${escapeODataString(listTitle)}')`);

// Same fields request against any "_api/web/"-relative list selector, so a list addressed by
// server-relative url reads its schema the same way one addressed by title does. `timeoutMs`
// bounds a request whose caller shares it with others (the client list's schema).
export const getFieldsByListEndpoint = async (accessToken: string, webUrl: string, listEndpoint: string, timeoutMs?: number): Promise<{ InternalName: string, Title: string, TypeAsString?: string }[]> => {
  try {
    // TypeAsString feeds resolveCustomFieldsColumnInternalName's type guard: a Lookup/User
    // column adopted by display name would 400 the whole $select items request downstream.
    return (await axiosRetryGet(`${webUrl}/_api/web/${listEndpoint}/fields?$select=InternalName,Title,TypeAsString&$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" },
        ...(timeoutMs ? { timeout: timeoutMs } : {})
      }))?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getFieldsByListEndpoint)");
  }
  return [];
};

export const getSiteLogo = async (accessToken: string, webUrl: string): Promise<string> => {
  try {
    const response: Response = await fetch(`${webUrl}/_api/siteiconmanager/getsitelogo?type='1'`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    });

    if (response.ok) {
      const url = window.URL || window.webkitURL;
      const blob = await response.blob();
      return url.createObjectURL(blob);
    }
  } catch (err) {
    // Ignore
  }
  return undefined;
};

export const getFileVersionData = async (accessToken: string, siteUrl: string, uniqueId: string): Promise<{ Level: number, MajorVersion: number, MinorVersion: number }> => {
  try {
    const response = (await axiosRetryGet(`${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')?$select=Level,MajorVersion,MinorVersion`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;

    if (response) {
      return {
        Level: response.Level,
        MajorVersion: response.MajorVersion,
        MinorVersion: response.MinorVersion
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getFileVersionData)");
  }
  return null;
};

export const getHubSites = async (accessToken: string, siteUrl: string): Promise<IHubSite[]> => {
  try {
    const response = (await axiosRetryGet(`${siteUrl}/_api/HubSites?$select=ID,SiteUrl,ParentHubSiteId,Title,Description`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;

    return Array.isArray(response?.value) && response.value.length > 0
      ? response.value.map(hubSite => ({ id: hubSite.ID, siteUrl: hubSite.SiteUrl, parentHubSiteId: hubSite.ParentHubSiteId, title: hubSite.Title, description: hubSite.Description }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getHubSites)");
  }
  return null;
};

export const getAccessibleSites = async (siteAbsUrl: string, queryText: string): Promise<ISiteInfo[]> => {
  if (siteAbsUrl) {
    try {
      // const queryText = siteQueries.top;
      const selectProperties = "'Title,Path,WebTemplate'";
      const rowLimit = 500;

      const apiUrl = `${siteAbsUrl}/_api/search/query?querytext='${queryText}'&selectproperties=${selectProperties}&rowlimit=${rowLimit}&trimduplicates=true`;

      const response = (await axiosRetryGet(apiUrl,
        {
          headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json;odata=nometadata" }
        }))?.data;

      const data: ISearchResponse = response;

      if (data && data.PrimaryQueryResult && data.PrimaryQueryResult.RelevantResults) {
        const relevantResults = data.PrimaryQueryResult.RelevantResults;
        const rows = relevantResults.Table.Rows;

        const sites: ISiteInfo[] = rows?.map((row) => {
          const cells = row.Cells as ISearchResultCell[];
          const siteName: string = getCellValue(cells, "Title");
          return {
            id: getCellValue(cells, "SiteId"),
            name: siteName,
            siteName: siteName,
            webUrl: getCellValue(cells, "Path"),
            isSite: true,
            isSubsite: false
          };
        });
        return sites ?? [];
      }
    } catch (error) {
      logger(error, "SharePointRestService.ts (getAccessibleSites)");
    }
  }
  return [];
};

export const createCopyJobs = async (args: ICreateCopyJobsRequest): Promise<{ success: boolean, jobs: any[], error?: string }> => {
  let errorMessage = "";
  try {
    const { siteUrl, files, sourceSiteUrl, destinationServerRelPath, isMoveMode, accessToken } = args;

    let destinationPath = siteUrl;
    if (destinationServerRelPath) {
      const domain = new URL(siteUrl).origin;
      // Escaped for the same reason as the source URIs, but NOT with the same function:
      // callers disagree about whether this path is already percent-encoded, so `%` must
      // be left alone here. See escapeDestinationPathChars. Otherwise deliberately left
      // unencoded (spaces and all), which SharePoint accepts today — a destination
      // containing no `#` or `?` is byte-identical to before this existed.
      destinationPath = `${domain}${escapeDestinationPathChars(destinationServerRelPath)}`;
    }

    const fileNames: string[] = files.map(file => file.FileLeafRef);
    const duplicateNames: string[] = fileNames.filter((fileName, index) => fileNames.indexOf(fileName) !== index);

    if (duplicateNames.length > 0) {
      return {
        success: false,
        jobs: [],
        error: CopyMoveErrors.DuplicateFilenames
      };
    }

    // A move into the folder the items already occupy is degenerate and CreateCopyJobs
    // rejects it with an opaque OData error. The UI guards against it only for
    // library-initiated operations (they know their single source folder); an aggregate
    // selection spans libraries, so this is the one place with both the destination and
    // every row's real source. Copy is exempt: NameConflictBehavior 2 keeps both.
    if (isMoveMode && destinationServerRelPath && destinationHoldsQueuedRows(destinationServerRelPath, files)) {
      return {
        success: false,
        jobs: [],
        error: CopyMoveErrors.SameSourceAndDestination
      };
    }

    // Source URLs anchor on the source SITE origin and each row's own FileRef. No
    // caller-supplied folder path takes part — see copyJobSourcePaths for the class
    // of failure that removed.
    const { paths, unresolved } = buildCopyJobSourcePaths(sourceSiteUrl, files);

    // Fail closed: proceeding with a partially-resolved batch would silently copy a
    // subset of the user's selection and report success.
    if (unresolved.length > 0) {
      return {
        success: false,
        jobs: [],
        error: CopyMoveErrors.UnresolvableSourcePaths
      };
    }

    const sourceLocations = Object.values(
      groupBy((item) => item.containerPath, paths)
    );

    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };

    let jobs: any[] = [];
    for (let i = 0; i < sourceLocations.length; i++) {
      const locationFiles = sourceLocations[i];
      if (Array.isArray(locationFiles) && locationFiles.length > 0) {
        const body = {
          exportObjectUris: locationFiles.map(file => {
            return file.filePath;
          }),
          destinationUri: destinationPath,
          options: {
            AllowSchemaMismatch: true,
            AllowSmallerVersionLimitOnDestination: true,
            IgnoreVersionHistory: true,
            IsMoveMode: isMoveMode,
            NameConflictBehavior: 2 //1 = REPLACE and 2 = KEEP BOTH
          }
        }

        const copyJobsResponse = (await axiosRetryPost(`${siteUrl}/_api/site/CreateCopyJobs`, body, requestConfig, false))?.data?.value;
        if (copyJobsResponse) {
          jobs = jobs.concat(copyJobsResponse);
        }
      }
    }

    if (jobs.length > 0) {
      return {
        success: true,
        jobs
      };
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (createCopyJobs)");
    errorMessage = err.response?.data["odata.error"]?.message?.value;
  }

  return {
    success: false,
    jobs: [],
    error: errorMessage
  };
};

const getPermissionsFromBase = (perms: IBasePermissions): currentSharepointFolderUserPermissions => {
  const userPermissions: currentSharepointFolderUserPermissions = {
    canAddListItem: sp.web.hasPermissions(perms, PermissionKind.AddListItems),
    canEditListItem: sp.web.hasPermissions(perms, PermissionKind.EditListItems),
    canDeleteListItem: sp.web.hasPermissions(perms, PermissionKind.DeleteListItems),
    canOpenItems: sp.web.hasPermissions(perms, PermissionKind.OpenItems),
    canViewVersions: sp.web.hasPermissions(perms, PermissionKind.ViewVersions),
    canDeleteVersions: sp.web.hasPermissions(perms, PermissionKind.DeleteVersions),
    canCancelCheckout: sp.web.hasPermissions(perms, PermissionKind.CancelCheckout),
    canApproveItems: sp.web.hasPermissions(perms, PermissionKind.ApproveItems),
    canEnumeratePermissions: sp.web.hasPermissions(perms, PermissionKind.EnumeratePermissions),
    canManagePermissions: sp.web.hasPermissions(perms, PermissionKind.ManagePermissions),
  };

  return userPermissions;
}

const getPermMaskH = (permHex: string) => {
  const a = permHex.length;
  return a <= 10 ? 0 : parseInt(permHex.substring(2, a - 8), 16);
};

const getPermMaskL = (permHex: string) => {
  const a = permHex.length;
  return a <= 10 ? parseInt(permHex) : parseInt(permHex.substring(a - 8, a), 16);
};

const groupBy = (groupingFn: (x: any) => string, arr: any[]) => {
  const groups: { [key: string]: any[] } = {}

  arr.forEach((item) => {
    const key = groupingFn(item);

    if (groups[key] !== undefined) {
      groups[key].push(item);
    } else {
      groups[key] = [item];
    }
  });

  return groups;
};

const getCellValue = (cells: ISearchResultCell[], key: string): string | undefined => {
  const cell = cells.find(c => c.Key === key);
  return cell ? cell.Value : undefined;
};
